import { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  ClickAwayListener,
  IconButton,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface Props {
  anchorEl: HTMLElement | null;
}

/**
 * Guidance popover shown on the Review & Submit page whenever a Sim Req Attachment
 * document is present, pointing at its menu item and explaining the Edit + Approve
 * workflow. It shows on every visit (the dismiss only hides it for the current
 * session) so ROs are always reminded to action the attachment.
 */
const SimRequestAttachmentOnboarding = ({ anchorEl }: Props): JSX.Element | null => {
  const [dismissed, setDismissed] = useState(false);

  const dismiss = useCallback(() => setDismissed(true), []);

  if (!anchorEl || dismissed) return null;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
      data-testid="sim-request-attachment-onboarding-popover"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2, width: 320 }}>
      <ClickAwayListener onClickAway={dismiss}>
        <Card elevation={6} sx={{ borderRadius: '8px' }}>
          <CardContent sx={{ paddingBottom: 0 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle1" color="text.primary" fontWeight={600} gutterBottom>
                Add your sim request attachments
              </Typography>
              <IconButton
                size="small"
                aria-label="Close"
                onClick={dismiss}
                data-testid="sim-request-attachment-onboarding-close-icon"
                sx={{ marginTop: '-4px', marginRight: '-8px' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Use the Edit button to upload any files or add additional text to the document. When you&apos;re done, use
              Approve in the toolbar to finalise.
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              color="primary"
              onClick={dismiss}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  dismiss();
                }
              }}
              data-testid="sim-request-attachment-onboarding-close">
              GOT IT
            </Button>
          </CardActions>
        </Card>
      </ClickAwayListener>
    </Popper>
  );
};

export default SimRequestAttachmentOnboarding;
